import { useEffect, useMemo, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ApiErrorMessage } from '@/components/atoms/ApiErrorMessage';
import { Button } from '@/components/atoms/Buttons';
import {
    ModalBody,
    ModalComponent,
    ModalFooter,
    ModalFooterStart,
} from '@/components/molecules/ModalComponent';
import SegmentedControl from '@/components/molecules/SegmentedControl';
import {
    type AccessLevel,
    type ApiUser,
    type CreateApiUserRequest,
    useCreateApiUser,
} from '@/domain/apiUsers';

import type { ClientLocation } from '../ClientContainer';

import { AccessScopeLists } from './AccessScopeLists';
import {
    ACCESS_LEVEL_DESCRIPTION_KEYS,
    locationReducer,
    makeInitialLocationsList,
    parseAccessLevelToId,
} from './apiUserLocations';
import { CopyToClipboardComponent } from './CopyToClipboard';

import styles from './CreateApiUserModal.module.scss';

interface CreateApiUserModalProps {
    clientId: number;
    isOpen: boolean;
    clientLocations: ClientLocation[];
    addCreatedApiUser: (apiUser: ApiUser) => void;
    closeModal: () => void;
}

export default function CreateApiUserModal(props: CreateApiUserModalProps) {
    const { clientId, isOpen, clientLocations, addCreatedApiUser, closeModal } = props;
    const { t } = useTranslation();

    const [selectedAccessLevel, setSelectedAccessLevel] = useState<AccessLevel>('client');
    const [createdApiUser, setCreatedApiUser] = useState<ApiUser | null>(null);
    const [generatedApiKey, setGeneratedApiKey] = useState<string | null>(null);

    const [state, dispatch] = useReducer(locationReducer, {
        locationsList: makeInitialLocationsList(clientLocations),
    });
    const { locationsList } = state;

    const {
        mutateAsync: createApiUser,
        isLoading: isCreatingApiUser,
        error: createApiUserError,
    } = useCreateApiUser();

    const disableAllButtons = useMemo(() => generatedApiKey !== null, [generatedApiKey]);

    const disabledCreateButton: boolean = useMemo(() => {
        // Matches what the request sends: the picked locations, and the picked pens of those.
        const isEmptyLocationsSelected =
            selectedAccessLevel === 'location' &&
            !locationsList.some((location) => location.selected);
        const isEmptyCagesSelected =
            selectedAccessLevel === 'cage' &&
            !locationsList.some(
                (location) => location.selected && location.cages.some((cage) => cage.selected)
            );
        const hasError = createApiUserError !== null;
        const hasGeneratedApiKey = generatedApiKey !== null;
        return (
            isCreatingApiUser ||
            hasError ||
            hasGeneratedApiKey ||
            isEmptyLocationsSelected ||
            isEmptyCagesSelected
        );
    }, [
        locationsList,
        isCreatingApiUser,
        createApiUserError,
        generatedApiKey,
        selectedAccessLevel,
    ]);

    const actionDescription = t(ACCESS_LEVEL_DESCRIPTION_KEYS[selectedAccessLevel]);

    const locationListItemsDisabled = useMemo(() => {
        return selectedAccessLevel === 'client';
    }, [selectedAccessLevel]);

    const cageListItemsDisabled = useMemo(() => {
        return selectedAccessLevel === 'client' || selectedAccessLevel === 'location';
    }, [selectedAccessLevel]);

    useEffect(() => {
        dispatch({ type: 'RESET', clientLocations });
    }, [clientLocations]);

    useEffect(() => {
        if (createdApiUser) {
            addCreatedApiUser(createdApiUser);
        }
    }, [createdApiUser, addCreatedApiUser]);

    const handleCreateApiUser = () => {
        const requestBody: CreateApiUserRequest = {
            accessLevelId: parseAccessLevelToId(selectedAccessLevel),
            allowedLocationIds: locationsList
                .filter((location) => location.selected && selectedAccessLevel === 'location')
                .map((location) => location.id),
            allowedCageIds: locationsList
                .filter((location) => location.selected && selectedAccessLevel === 'cage')
                .flatMap((location) =>
                    location.cages.filter((cage) => cage.selected).map((cage) => cage.id)
                ),
        };
        createApiUser({ clientId, requestBody }).then(
            (createdApiUser) => {
                const mappedCreatedApiUser: ApiUser = {
                    id: createdApiUser.id,
                    createdAt: createdApiUser.createdAt,
                    lastUsed: createdApiUser.lastUsed,
                    accessLevel: createdApiUser.accessLevel,
                    allowedLocationIds: createdApiUser.allowedLocationIds || [],
                    allowedCageIds: createdApiUser.allowedCageIds || [],
                };
                setCreatedApiUser(mappedCreatedApiUser);
                setGeneratedApiKey(createdApiUser.password);
            },
            () => {
                // The hook's `error` already drives the message under the form.
            }
        );
    };

    const handleSelectAccessLevel = (accessLevel: AccessLevel) => {
        setSelectedAccessLevel(accessLevel);
        dispatch({ type: 'RESET', clientLocations });
    };

    const handleCloseModal = () => {
        setCreatedApiUser(null);
        setGeneratedApiKey(null);
        setSelectedAccessLevel('client');
        dispatch({ type: 'RESET', clientLocations });
        closeModal();
    };

    return (
        <ModalComponent
            contentClassName={styles.createModalContainer}
            isOpen={isOpen}
            onClose={handleCloseModal}
            dismissible={false}
            onClosed={handleCloseModal}
            centered={true}
            size="lg"
            title={t('internal.createApiUser.title')}>
            <ModalBody>
                <div className={styles.createModalBodyContainer}>
                    <div className={styles.bodyActionsContainer}>
                        <div>{t('internal.createApiUser.accessLevel')}</div>
                        <SegmentedControl<AccessLevel>
                            aria-label={t('internal.createApiUser.accessLevel')}
                            orientation="vertical"
                            value={selectedAccessLevel}
                            onChange={(accessLevel) => handleSelectAccessLevel(accessLevel)}
                            options={[
                                {
                                    value: 'client',
                                    label: t('common.labels.client'),
                                    disabled: disableAllButtons,
                                },
                                {
                                    value: 'location',
                                    label: t('common.labels.location'),
                                    disabled: disableAllButtons,
                                },
                                {
                                    value: 'cage',
                                    label: t('common.labels.cage'),
                                    disabled: disableAllButtons,
                                },
                            ]}
                        />
                        <div className={styles.actionsDescription}>{actionDescription}</div>
                    </div>
                    <AccessScopeLists
                        locationsList={locationsList}
                        locationListItemsDisabled={locationListItemsDisabled}
                        cageListItemsDisabled={cageListItemsDisabled}
                        disableAllButtons={disableAllButtons}
                        dispatch={dispatch}
                    />
                </div>
            </ModalBody>
            <ModalFooter>
                <ModalFooterStart>
                    <div>
                        {isCreatingApiUser && <div>{t('internal.createApiUser.creating')}</div>}
                        {createApiUserError && (
                            <div>
                                <ApiErrorMessage
                                    error={createApiUserError}
                                    fallback={t('internal.createApiUser.createFailed')}
                                    formatRefusal={(message) =>
                                        t('internal.createApiUser.createError', { message })
                                    }
                                />
                            </div>
                        )}
                        {generatedApiKey && (
                            <div className={styles.generatedApiKeyContainer}>
                                <CopyToClipboardComponent
                                    title={t('internal.createApiUser.userId')}
                                    textToCopy={createdApiUser?.id.toString() || ''}
                                />
                                <CopyToClipboardComponent
                                    title={t('internal.createApiUser.apiKey')}
                                    textToCopy={generatedApiKey}
                                />
                            </div>
                        )}
                    </div>
                </ModalFooterStart>
                <Button
                    disabled={disabledCreateButton}
                    variant="primary"
                    onClick={handleCreateApiUser}>
                    {t('internal.client.create')}
                </Button>
            </ModalFooter>
        </ModalComponent>
    );
}
